import { Star } from "lucide-react";
import { useEffect, useRef } from "react";

const REVIEWS_DATA = [
  { text: "Quick service and professional work.", name: "Alex", location: "Lincoln Park", job: "Homeowner", rating: 5 },
  { text: "Arrived fast and fixed everything.", name: "Sarah", location: "Lincoln Park", job: "Tenant", rating: 4 },
  { text: "Very reliable local plumbers.", name: "John", location: "Lincoln Park", job: "Landlord", rating: 5 },
  { text: "Great service from start to finish.", name: "Michael", location: "Lincoln Park", job: "Homeowner", rating: 5 },
  { text: "Fast response and great service.", name: "Maya", location: "Wicker Park", job: "Tenant", rating: 4 },
  { text: "Very clean and professional work.", name: "Omar", location: "Wicker Park", job: "Homeowner", rating: 5 },
  { text: "Solved the issue quickly.", name: "Leah", location: "Wicker Park", job: "Manager", rating: 5 },
  { text: "Friendly and reliable plumbers.", name: "Daniel", location: "Wicker Park", job: "Homeowner", rating: 4 },
];

const GAP = 20;

function ReviewCard({ review, refProp }) {
  return (
    <div
      ref={refProp}
      className="min-w-[280px] bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-4"
    >
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className="w-4 h-4"
            fill={i < review.rating ? "#2563eb" : "#e5e7eb"}
            stroke="none"
          />
        ))}
      </div>

      <p className="text-gray-700 text-sm leading-relaxed flex-1">
        {review.text}
      </p>

      <div className="flex items-center gap-2.5 pt-1 border-t border-gray-100">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <span className="text-blue-600 font-black text-xs">
            {review.name[0]}
          </span>
        </div>
        <div>
          <p className="font-bold text-gray-900 text-sm leading-tight">
            {review.name}
          </p>
          <p className="text-xs text-gray-400">
            {review.location} · {review.job}
          </p>
        </div>
      </div>
    </div>
  );
}

function ReviewBand({ reviews, direction = "ltr" }) {
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const xRef = useRef(0);

  useEffect(() => {
    let animId;
    const speed = direction === "rtl" ? 0.6 : -0.6;

    function tick() {
      const cardWidth = (cardRef.current?.offsetWidth ?? 280) + GAP;
      const loopWidth = cardWidth * (reviews.length / 2);

      xRef.current += speed;

      if (xRef.current <= -loopWidth) xRef.current += loopWidth;
      if (xRef.current >= 0) xRef.current -= loopWidth;

      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${xRef.current}px)`;
      }

      animId = requestAnimationFrame(tick);
    }

    tick();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div className="overflow-hidden w-full">
      <div ref={trackRef} className="flex" style={{ gap: `${GAP}px` }}>
        {reviews.map((review, i) => (
          <ReviewCard
            key={i}
            review={review}
            refProp={i === 0 ? cardRef : null}
          />
        ))}
      </div>
    </div>
  );
}

export default function Reviews() {
  const doubled = [...REVIEWS_DATA, ...REVIEWS_DATA];

  return (
    <section id="reviews" className="max-w-[90%] xl:max-w-6xl mx-auto space-y-14">

      <div className="space-y-4">
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
          Reviews
        </div>

        <h2 className="font-black text-gray-900 text-4xl lg:text-5xl tracking-tight leading-tight">
          What People Say
        </h2>

        <p className="text-gray-500 text-lg leading-relaxed max-w-xl">
          Every review reflects our commitment to honest, professional service.
        </p>
      </div>

      <div className="space-y-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <ReviewBand reviews={doubled} direction="ltr" />
        <ReviewBand reviews={[...doubled].reverse()} direction="rtl" />
      </div>

    </section>
  );
}